import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FormAlert } from "@/components/common/Alert";
import { api, errorMessage } from "@/lib/api";
import { copyText } from "@/lib/catalogue";
import { formatDateTime } from "@/lib/format";

const METHOD_LABEL = { user: "matched by user", time: "matched by time", duplicate: "already recorded" };

const RecentCalls = ({ calls }) => (
  <section className="card-elevated p-5" data-testid="webhook-recent">
    <p className="font-medium">Recent calls</p>
    <p className="mt-1 text-xs text-muted-foreground">The last 20 calls. Only the time, type, match and run row are kept; inputs and outputs are dropped on arrival.</p>
    {calls.length === 0 ? <p className="mt-3 text-sm text-muted-foreground" data-testid="webhook-recent-empty">No calls yet.</p> : (
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-xs text-muted-foreground"><tr><th className="py-2 pr-3 font-medium">Time</th><th className="py-2 pr-3 font-medium">Type</th><th className="py-2 pr-3 font-medium">Result</th><th className="py-2 pr-3 font-medium">Item</th><th className="py-2 font-medium">Run row</th></tr></thead>
          <tbody>{calls.map((c) => (
            <tr key={c.id} className="border-t" data-testid={`webhook-call-${c.id}`}>
              <td className="py-2 pr-3 whitespace-nowrap">{formatDateTime(c.at)}</td>
              <td className="py-2 pr-3">{c.kind === "workflow" ? "Workflow" : "Chatbot"}</td>
              <td className="py-2 pr-3" data-testid={`webhook-call-result-${c.id}`}>{c.matched ? `Matched, ${METHOD_LABEL[c.method] || c.method}` : `Unmatched${c.reason ? `: ${c.reason}` : ""}`}</td>
              <td className="py-2 pr-3">{c.target_id || c.workflow_title || "None"}</td>
              <td className="py-2"><code className="text-xs">{c.run_id || "None"}</code></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    )}
  </section>
);

export const WebhooksAdmin = () => {
  const [hooks, setHooks] = useState(null);
  const [calls, setCalls] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/admin/webhooks").then(({ data }) => setHooks(data)).catch((e) => setError(errorMessage(e)));
    api.get("/admin/webhooks/recent").then(({ data }) => setCalls(data)).catch(() => setCalls([]));
  }, []);
  if (error) return <FormAlert>{error}</FormAlert>;
  if (!hooks) return <Skeleton className="h-40 rounded-xl" />;
  return (
    <div className="max-w-4xl space-y-4" data-testid="admin-webhooks">
      <p className="text-sm text-muted-foreground">Paste these into MindPal: the first in each chatbot's conversation webhook, the second in each workflow's Webhook node. The secret in the address proves the call came from MindPal. Chatbot runs are matched on the user ID and the slug. Workflow runs are matched on the MindPal workflow id (or title) set in Admin, Catalogue: the oldest open run of that item started in the last 6 hours is finished first. Runs are only marked finished; no input or output is stored. Open runs older than 24 hours are marked abandoned.</p>
      {hooks.map((h) => (
        <div key={h.kind} className="card-elevated p-5" data-testid={`webhook-${h.kind}`}>
          <p className="font-medium">{h.label}</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 break-all rounded-lg bg-muted px-3 py-2 text-xs" data-testid={`webhook-url-${h.kind}`}>{h.url}</code>
            <Button variant="secondary" size="sm" onClick={async () => toast[(await copyText(h.url)) ? "success" : "error"]("Webhook address copied")} data-testid={`webhook-copy-${h.kind}`}><Copy className="!h-4 !w-4" /> Copy</Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground" data-testid={`webhook-last-${h.kind}`}>Last received: {h.last_received_at ? `${formatDateTime(h.last_received_at)}${h.last_matched ? ", matched a run" : ", no matching run"}` : "Never"}</p>
        </div>
      ))}
      <RecentCalls calls={calls} />
    </div>
  );
};

export const PromptSeedImport = () => {
  const ref = useRef(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const upload = (file) => {
    if (!file) return;
    setError("");
    setResult(null);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed = JSON.parse(reader.result);
        const entries = Array.isArray(parsed) ? parsed : parsed.prompts || parsed.entries || parsed.seeds;
        if (!Array.isArray(entries)) throw new Error("shape");
        const { data } = await api.post("/admin/prompts/seed", { filename: file.name, entries });
        setResult({ ...data, file: file.name });
        toast.success(`${data.added} seed prompts added`);
      } catch (e) {
        setError(e.response ? errorMessage(e) : "That file is not a list of prompts in JSON. Check it and try again.");
      } finally {
        if (ref.current) ref.current.value = "";
      }
    };
    reader.readAsText(file);
  };
  return (
    <section className="card-elevated p-6 md:p-8" data-testid="prompt-seed-import">
      <h2 className="text-base font-semibold">Prompt seeds</h2>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Upload a "prompt-seeds - team.json" file (title, prompt, for, tags, why_it_worked). Entries are added to the Prompt Hub authored by Hub seed. Re-uploading skips entries already there.</p>
      <input ref={ref} type="file" accept="application/json,.json" className="sr-only" onChange={(e) => upload(e.target.files?.[0])} data-testid="prompt-seed-input" />
      <Button variant="secondary" className="mt-4" onClick={() => ref.current?.click()} data-testid="prompt-seed-button"><UploadCloud className="!h-4 !w-4" /> Upload seed file</Button>
      <div className="mt-4"><FormAlert testid="prompt-seed-error">{error}</FormAlert></div>
      {result && (
        <p className="mt-2 text-sm" data-testid="prompt-seed-result">
          {result.file}: {result.added} added, {result.skipped} already there{result.errors.length ? `, ${result.errors.length} skipped with problems: ${result.errors.join("; ")}` : ""}.
        </p>
      )}
    </section>
  );
};
